import { Select } from 'antd';
import { Controller, useFormState } from 'react-hook-form';
import { ICommonFormProps } from '../DynamicForm.types';
import { isFormValidationError } from '@src/utilities/form';
import { ETypographyIntents } from '@src/typings';
import { StyledError } from '@src/components/ui/Input/styles';
import { FormLabel } from '@src/components/ui';
import { useCallback } from 'react';

export function FormSelect(props: Readonly<ICommonFormProps>) {
    const {
        label,
        keyName,
        control,
        registerOptions,
        inputProperties,
        formProps,
        disabled,
        className,
    } = props;

    const { touchedFields, isSubmitted } = useFormState({ control });

    const createHandleChange = useCallback(
        (onChange: (value: any) => void) => (value: any) => {
            onChange(value ?? null);
            if (registerOptions?.onChange) {
                registerOptions.onChange({
                    target: { value },
                } as any);
            }
        },
        [registerOptions]
    );

    return (
        <div className={className}>
            <Controller
                control={control}
                name={keyName}
                rules={registerOptions}
                render={({ field, fieldState: { error: fieldStateError } }) => {
                    // Use filtered errors prop if available, otherwise use fieldState error
                    // But only show if field has been touched or form submitted
                    const error = props.errors ?? fieldStateError;
                    const showError = Boolean(
                        error && (touchedFields[keyName] || isSubmitted)
                    );
                    const errorMessage = showError
                        ? isFormValidationError(error?.type)
                            ? error?.message
                            : `${label} ${error?.type}`
                        : undefined;

                    return (
                        <>
                            {label && (
                                <FormLabel
                                    label={label}
                                    required={
                                        typeof registerOptions?.required ===
                                        'object'
                                            ? Boolean(
                                                  registerOptions.required.value
                                              )
                                            : Boolean(registerOptions?.required)
                                    }
                                />
                            )}
                            <Select
                                className="w-full !h-10 text-base"
                                size={inputProperties?.size}
                                status={showError ? 'error' : undefined}
                                value={
                                    field.value ??
                                    formProps?.defaultValue ??
                                    null
                                }
                                onChange={createHandleChange(field.onChange)}
                                options={formProps?.selectOptions}
                                filterOption={formProps?.filterOption}
                                showSearch={formProps?.showSearch}
                                placeholder={
                                    (inputProperties?.placeholder as string) ||
                                    (formProps?.placeholder as string)
                                }
                                allowClear={formProps?.allowClear}
                                disabled={disabled || false}
                            />

                            {showError && (
                                <StyledError intent={ETypographyIntents.Badge}>
                                    {errorMessage}
                                </StyledError>
                            )}
                        </>
                    );
                }}
            />
        </div>
    );
}
